/* =====================================================================
   SVPPLY — theme.js
   Cart drawer open/close, AJAX add to cart, variant picker, line-item
   quantity changes via Shopify's /cart/*.js endpoints.
   ===================================================================== */
(function(){
  'use strict';

  /* ---------- DOM helpers ---------- */
  const $  = (sel, el = document) => el.querySelector(sel);
  const $$ = (sel, el = document) => [...el.querySelectorAll(sel)];

  /* ---------- CART DRAWER ---------- */
  const drawer       = $('[data-cart-drawer]');
  const drawerScrim  = $('[data-cart-drawer-scrim]');
  const drawerBody   = $('[data-cart-drawer-body]');

  function openDrawer(){
    if(!drawer) return;
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden','false');
    drawerScrim?.classList.add('open');
    document.body.style.overflow = 'hidden';
  }
  function closeDrawer(){
    if(!drawer) return;
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden','true');
    drawerScrim?.classList.remove('open');
    document.body.style.overflow = '';
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-drawer-open]')){ e.preventDefault(); openDrawer(); return; }
    if (e.target.closest('[data-cart-drawer-close]') || e.target === drawerScrim){ closeDrawer(); return; }
  });
  document.addEventListener('keydown', (e) => { if(e.key === 'Escape') closeDrawer(); });

  /* ---------- CART API HELPERS ---------- */
  async function fetchCart(){
    const r = await fetch('/cart.js', { credentials:'same-origin' });
    return r.json();
  }
  async function addItem(id, qty=1){
    const r = await fetch('/cart/add.js', {
      method:'POST',
      credentials:'same-origin',
      headers:{ 'Content-Type':'application/json', Accept:'application/json' },
      body: JSON.stringify({ id, quantity: qty })
    });
    if(!r.ok){ throw new Error(`add failed: ${r.status}`); }
    return r.json();
  }
  async function changeLine(line, qty){
    const r = await fetch('/cart/change.js', {
      method:'POST',
      credentials:'same-origin',
      headers:{ 'Content-Type':'application/json', Accept:'application/json' },
      body: JSON.stringify({ id: line, quantity: qty })
    });
    return r.json();
  }
  async function rerenderCartDrawer(){
    const r = await fetch('/?section_id=cart-drawer', { credentials:'same-origin' });
    const html = await r.text();
    const next = new DOMParser().parseFromString(html, 'text/html');
    const nextDrawer = next.querySelector('[data-cart-drawer]');
    const nextScrim  = next.querySelector('[data-cart-drawer-scrim]');
    if(nextDrawer && drawer){
      drawer.innerHTML = nextDrawer.innerHTML;
    }
    syncCartCounts();
  }
  async function syncCartCounts(){
    const cart = await fetchCart();
    $$('[data-cart-count-display]').forEach(n => n.textContent = cart.item_count);
    document.body.dataset.cartCount = cart.item_count;
  }

  /* ---------- ADD TO CART (intercept product form) ---------- */
  const productForm = $('[data-product-form]');
  if(productForm){
    productForm.addEventListener('submit', async (e) => {
      const submitter = e.submitter;
      // "Buy now" button: let it submit natively → Shopify takes us straight to checkout
      if(submitter && submitter.name === 'checkout'){ return; }
      e.preventDefault();
      const select = $('#variant-select', productForm);
      if(!select || !select.value){ alert('Select a variant first.'); return; }
      try{
        await addItem(select.value, 1);
        await rerenderCartDrawer();
        openDrawer();
      } catch(err){
        console.error('Add to cart failed', err);
        alert('Could not add to cart — that variant may be out of stock.');
      }
    });
  }

  /* ---------- LINE ITEM CHANGES (drawer + cart page) ---------- */
  document.addEventListener('click', async (e) => {
    const inc = e.target.closest('[data-line-increase]');
    const dec = e.target.closest('[data-line-decrease]');
    const rm  = e.target.closest('[data-line-remove]');
    if(!inc && !dec && !rm) return;
    e.preventDefault();
    const key = (inc || dec || rm).dataset.lineIncrease || (inc || dec || rm).dataset.lineDecrease || (inc || dec || rm).dataset.lineRemove;
    if(!key) return;
    try{
      const cart = await fetchCart();
      const item = cart.items.find(i => i.key === key);
      if(!item) return;
      let nextQty = item.quantity;
      if(inc) nextQty++;
      else if(dec) nextQty = Math.max(0, nextQty - 1);
      else if(rm) nextQty = 0;
      await changeLine(key, nextQty);
      await rerenderCartDrawer();
      // If we're on the full /cart page, reload to update rows
      if (document.body.classList.contains('template-cart')) {
        window.location.reload();
      }
    } catch(err){ console.error('Cart change failed', err); }
  });

  /* ---------- VARIANT PICKER (product page) ---------- */
  const productJsonScript = $('[data-product-json]');
  if(productJsonScript && productForm){
    const product = JSON.parse(productJsonScript.textContent);
    const select  = $('#variant-select', productForm);

    function currentSelection(){
      // Read each option from its row of swatches (sorted by option index)
      const rows = $$('.swatch-row', productForm)
        .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex));
      return rows.map(row => row.querySelector('.swatch.selected')?.dataset.optionValue || null);
    }
    function findVariant(values){
      return product.variants.find(v =>
        v.options.length === values.length &&
        v.options.every((o, i) => o === values[i])
      );
    }
    function refresh(){
      const sel = currentSelection();
      const variant = findVariant(sel);
      if(!variant) return;
      select.value = variant.id;
      const priceEl = $('#product-price');
      if(priceEl){
        // Shopify prices are in cents
        const cents = variant.price;
        const dollars = (cents / 100).toFixed(2);
        priceEl.innerHTML = `$${dollars} <span class="currency-code">${document.body.dataset.currency || 'USD'}</span>`;
      }
      const stockEl = $('[data-stock-indicator]');
      if(stockEl){
        stockEl.classList.remove('in','low','out');
        let copy = 'In stock';
        if(!variant.available){
          stockEl.classList.add('out');
          copy = 'Sold out';
        } else if(typeof variant.inventory_quantity === 'number' && variant.inventory_quantity > 0 && variant.inventory_quantity < 5){
          stockEl.classList.add('low');
          copy = `Only ${variant.inventory_quantity} left — Restock TBD`;
        } else if(variant.inventory_quantity > 0){
          copy = `${variant.inventory_quantity} in stock`;
        } else {
          copy = 'In stock — ready to ship';
        }
        const dot = stockEl.querySelector('.dot');
        stockEl.innerHTML = '';
        if(dot) stockEl.appendChild(dot);
        stockEl.append(' ' + copy);
      }
      // Swap main image to the variant's featured_image when present
      if(variant.featured_image && variant.featured_image.src){
        const main = $('#main-product-image');
        if(main) main.src = variant.featured_image.src.replace(/\.(jpg|jpeg|png|webp)$/, '_1200x.$1');
      }
      const addBtn = $('#add-to-cart');
      if(addBtn){
        addBtn.disabled = !variant.available;
      }
    }

    // Click on a swatch → enforce single-select within its row.
    // Uses .closest('.swatch-row') to find the parent row directly, then
    // strips .selected from EVERY swatch in that row before marking the
    // clicked one — bulletproof against any selector/attribute mismatch.
    productForm.addEventListener('click', (e) => {
      const sw = e.target.closest('.swatch');
      if(!sw) return;
      e.preventDefault();
      e.stopPropagation();
      if(sw.disabled || sw.classList.contains('unavailable')) return;

      const row = sw.closest('.swatch-row');
      if(!row) return;

      // Clear ALL selected siblings in this exact row, no matter how many.
      row.querySelectorAll('.swatch.selected').forEach(b => {
        b.classList.remove('selected');
        b.setAttribute('aria-checked', 'false');
      });

      // Mark the clicked one.
      sw.classList.add('selected');
      sw.setAttribute('aria-checked', 'true');

      // Update the readout next to the option name ("Size — M")
      const idx = sw.dataset.optionIndex;
      const currentDisplay = productForm.querySelector(`[data-option-current="${idx}"]`);
      if(currentDisplay) currentDisplay.textContent = sw.dataset.optionValue;

      refresh();
    });

    refresh();
  }

  /* (Gallery doesn't use thumbnails anymore — every product image is stacked
     vertically at full column width, so there's nothing to click-swap.) */

  /* ---------- GRID SEARCH (client-side filter on featured/collection pages) ---------- */
  const gridSearch = $('#grid-search');
  if(gridSearch){
    gridSearch.addEventListener('input', () => {
      const q = gridSearch.value.toLowerCase().trim();
      $$('.grid .card').forEach(card => {
        const name = card.querySelector('.name')?.textContent.toLowerCase() || '';
        card.style.display = !q || name.includes(q) ? '' : 'none';
      });
    });
  }

  /* ---------- INITIAL SYNC ---------- */
  syncCartCounts().catch(()=>{});

})();
